import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { attachmentService } from '../../services/financeService';

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

/**
 * Reusable attachment panel.
 *
 * When entityId is provided  → live mode (upload/download/delete immediately).
 * When entityId is null/undefined → staging mode (files are held in memory;
 *   parent calls ref.uploadPending(entityType, entityId) after save).
 */
const AttachmentPanel = forwardRef(({ entityType, entityId }, ref) => {
  const [attachments, setAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const fileRef = useRef(null);

  const isLive = !!(entityType && entityId);

  useEffect(() => {
    if (isLive) loadAttachments();
  }, [entityType, entityId]);

  useImperativeHandle(ref, () => ({
    uploadPending: async (type, id) => {
      if (pendingFiles.length === 0) return;
      for (const file of pendingFiles) {
        await attachmentService.upload(file, type, id);
      }
      setPendingFiles([]);
    },
    hasPending: () => pendingFiles.length > 0,
  }));

  const loadAttachments = async () => {
    try {
      const res = await attachmentService.list(entityType, entityId);
      if (res.success) setAttachments(res.data || []);
    } catch (e) {
      setError('Failed to load attachments');
    }
  };

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;

    if (isLive) {
      setUploading(true);
      setError(null);
      try {
        for (const file of files) {
          const res = await attachmentService.upload(file, entityType, entityId);
          if (!res.success) throw new Error(res.error || 'Upload failed');
        }
        await loadAttachments();
      } catch (e) {
        setError(e.message);
      } finally {
        setUploading(false);
        if (fileRef.current) fileRef.current.value = '';
      }
    } else {
      setPendingFiles(prev => [...prev, ...Array.from(files)]);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDelete = (id) => setDeleteConfirmId(id);
  const doDelete = async () => {
    const id = deleteConfirmId;
    setDeleteConfirmId(null);
    try {
      await attachmentService.delete(id);
      setAttachments(prev => prev.filter(a => a.id !== id));
    } catch (e) {
      setError('Failed to delete attachment');
    }
  };

  const removePending = (idx) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const allItems = [
    ...attachments.map(a => ({ type: 'saved', data: a })),
    ...pendingFiles.map((f, i) => ({ type: 'pending', data: f, idx: i })),
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--color-neutral-700)' }}>
          Attachments {allItems.length > 0 && `(${allItems.length})`}
        </span>
      </div>

      {error && (
        <div style={{ background: 'var(--color-error-light)', color: 'var(--color-error-strong)', padding: '6px 10px', borderRadius: 6, fontSize: 12, marginBottom: 8 }}>
          {error}
        </div>
      )}

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        style={{
          border: `2px dashed ${dragOver ? 'var(--color-primary)' : 'var(--color-border-strong)'}`,
          borderRadius: 8,
          padding: '12px 16px',
          textAlign: 'center',
          cursor: 'pointer',
          background: dragOver ? 'var(--color-info-light)' : 'var(--color-neutral-50)',
          transition: 'all 0.15s ease',
          marginBottom: allItems.length > 0 ? 10 : 0,
          fontSize: 13,
          color: 'var(--color-text-secondary)',
        }}
      >
        {uploading ? 'Uploading...' : 'Drop files here or click to upload'}
        <div style={{ fontSize: 11, marginTop: 2, opacity: 0.7 }}>PDF, images, Excel, CSV — max 10 MB</div>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.xlsx,.xls,.csv"
          style={{ display: 'none' }}
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      {allItems.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {allItems.map((item, i) => {
            if (item.type === 'saved') {
              const att = item.data;
              return (
                <div key={`s-${att.id}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', borderRadius: 6, background: 'var(--color-bg-tertiary)', fontSize: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <span style={{ fontSize: 16 }}>
                      {att.contentType?.includes('pdf') ? '\u{1F4C4}' : att.contentType?.includes('image') ? '\u{1F5BC}' : '\u{1F4CE}'}
                    </span>
                    <a href={attachmentService.downloadUrl(att.id)} target="_blank" rel="noopener noreferrer"
                      style={{ color: 'var(--color-info-strong)', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={att.originalName}>
                      {att.originalName}
                    </a>
                    <span style={{ color: 'var(--color-text-tertiary)', flexShrink: 0 }}>{formatSize(att.fileSize)}</span>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(att.id); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error)', fontSize: 14, padding: '2px 6px', borderRadius: 4 }}
                    title="Delete">&times;</button>
                </div>
              );
            } else {
              const file = item.data;
              return (
                <div key={`p-${item.idx}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', borderRadius: 6, background: 'var(--color-info-light)', border: '1px solid var(--color-info-border)', fontSize: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <span style={{ fontSize: 16 }}>{'\u{1F4CE}'}</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                    <span style={{ color: 'var(--color-text-tertiary)', flexShrink: 0 }}>{formatSize(file.size)}</span>
                    <span style={{ background: 'var(--color-info-light)', color: 'var(--color-info-strong)', padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600 }}>pending</span>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); removePending(item.idx); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error)', fontSize: 14, padding: '2px 6px', borderRadius: 4 }}
                    title="Remove">&times;</button>
                </div>
              );
            }
          })}
        </div>
      )}
      {deleteConfirmId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setDeleteConfirmId(null)}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 340, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
            <p style={{ fontSize: 15, color: 'var(--color-neutral-700)', marginBottom: 20 }}>Delete this attachment? This cannot be undone.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button style={{ padding: '7px 18px', borderRadius: 6, border: '1px solid var(--color-border-strong)', background: 'var(--color-neutral-50)', cursor: 'pointer', fontSize: 13 }} onClick={() => setDeleteConfirmId(null)}>Cancel</button>
              <button style={{ padding: '7px 18px', borderRadius: 6, border: 'none', background: 'var(--color-error)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }} onClick={doDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default AttachmentPanel;
